import { Icon } from "@iconify/react";
import { ThemeStyle } from "../../../../hooks/useTheme";
import {
  AttachmentSourceType,
  Event,
  MassTextAttachmentId,
  MassTextAttachmentType,
  MembershipState,
  PlanTypeV4,
} from "@markit/common.types";
import React, { useCallback, useMemo, useState } from "react";
import {
  hasSubscription,
  hasSubscriptionUnlockedAdvancedData,
  hostingLiveEvents,
  isApprovedPhoneStatus,
  isProductWithCheckout,
} from "@markit/common.utils";
import {
  MEDIA_UPLOAD_CODES,
  errorAlertText,
  errorSubAlertText,
  uploadMedia,
} from "../../../../utils/photoUtils";
import AlertContainer from "../../../Containers/AlertContainer";
import { getEventState } from "../../../../redux/slices/eventSlice";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import { DropdownMenu, DropdownMenuItem } from "../../../DropdownMenu";
import { notificationActions } from "../../../../redux/slices/notificationSlice";

type AttachmentsActionBarHoldItemProps = {
  onClick: (key: string) => void;
  attachmentSourceType: AttachmentSourceType;
  theme?: ThemeStyle;
  invertedColor?: boolean;
  disabledEventLinks?: boolean;
  event?: Event;
  setImage?: (image: string) => void;
  fileInputRef?: React.MutableRefObject<HTMLInputElement | null>;
};

export const AttachmentsActionBarHoldItem = (
  props: AttachmentsActionBarHoldItemProps
) => {
  const {
    onClick,
    attachmentSourceType,
    theme,
    invertedColor,
    disabledEventLinks,
    setImage,
    fileInputRef,
  } = props;
  const dispatch = useDispatch();
  const { accountData } = useSelector(getAccountState).account;
  const { events } = useSelector(getEventState).events;
  const [alertText, setAlertText] = useState("");
  const [alertSubText, setAlertSubText] = useState("");

  const creatorHasProducts = useMemo(
    () =>
      hostingLiveEvents(events, accountData.uid, false, false, true).some(
        (event) => isProductWithCheckout(event.eventType, event.accessLevel)
      ),
    [accountData.uid, events]
  );

  /*  image selecting functions */
  const uploadImage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      return new Promise<void>((resolve, reject) => {
        (async () => {
          if (setImage) {
            const uploadData = await uploadMedia(event, false, true);
            if (uploadData.uploadCode !== MEDIA_UPLOAD_CODES.SUCCESS) {
              setAlertText(errorAlertText(uploadData.uploadCode, true));
              setAlertSubText(errorSubAlertText(uploadData.uploadCode));
              reject();
            } else {
              setImage(uploadData.blobUrl);
              resolve();
            }
          } else {
            // no set image function
            reject();
          }
        })().catch(reject); // Handle any async errors
      });
    },
    [setAlertText, setImage]
  );
  /*  END image selecting functions */

  const hasPaidMembership = useMemo(
    () =>
      accountData.membership.state === MembershipState.ACTIVE &&
      accountData.membership.membershipPlans.some((plan) => plan.price > 0),
    [accountData.membership.membershipPlans, accountData.membership.state]
  );

  const items: DropdownMenuItem[] = useMemo(() => {
    const menuItems: DropdownMenuItem[] = [];

    menuItems.push(
      {
        title: "Link",
        icon: "mdi:link-variant",
        key: "link",
        isDestructive: false,
        onPress: () => onClick(MassTextAttachmentType.TRACKING_LINK),
      },
      {
        title: "Form",
        icon: "ion:document-text",
        key: "form",
        isDestructive: false,
        onPress: () => onClick(MassTextAttachmentType.GENERIC_LINK),
      },
      {
        title: "Post",
        icon: "ion:newspaper",
        key: "post",
        isDestructive: false,
        onPress: () => onClick(MassTextAttachmentType.POST_LINK),
      },
      {
        title: "Product",
        icon: "ion:bag-handle",
        key: "product",
        isDestructive: false,
        onPress: () => onClick(MassTextAttachmentType.PRODUCT_LINK),
      },
      {
        title: "Event",
        icon: "ion:calendar",
        key: "event",
        isDestructive: false,
        onPress: () => onClick(MassTextAttachmentType.LINK),
      }
    );

    if (attachmentSourceType === AttachmentSourceType.DM_AUTOMATION) {
      menuItems.push({
        title: "Link to Subscribe",
        icon: "ion:person-circle",
        key: "link_to_subscribe",
        isDestructive: false,
        onPress: () => onClick(MassTextAttachmentId.SUBSCRIBE_LINK),
      });
    } else {
      // Only show ticket options if on conversations
      if (
        attachmentSourceType === AttachmentSourceType.CONVERSATION &&
        creatorHasProducts
      ) {
        menuItems.push({
          title: "Order Link",
          icon: disabledEventLinks ? "ion:alert-circle" : "ion:ticket",
          key: MassTextAttachmentId.TICKET_LINK,
          isDestructive: false,
          onPress: () =>
            disabledEventLinks
              ? alert("You cannot add a order link in this case.")
              : onClick(MassTextAttachmentId.TICKET_LINK),
        });

        menuItems.push({
          title: "Calendar Invite",
          icon: disabledEventLinks
            ? "ion:alert-circle"
            : "mdi:calendar-account",
          key: MassTextAttachmentId.CALENDAR_INVITE,
          isDestructive: false,
          onPress: () =>
            disabledEventLinks
              ? alert("You cannot add a calendar invite in this case.")
              : onClick(MassTextAttachmentId.CALENDAR_INVITE),
        });
      }

      if (
        attachmentSourceType !== AttachmentSourceType.CONVERSATION &&
        hasSubscription(accountData) &&
        isApprovedPhoneStatus(accountData)
      ) {
        menuItems.push({
          title: "Contact Card",
          icon: "ion:id-card",
          key: "contact_card",
          isDestructive: false,
          onPress: () => onClick(MassTextAttachmentId.CONTACT_CARD),
          disabled:
            !hasSubscription(accountData) ||
            !isApprovedPhoneStatus(accountData),
        });
      }

      if (hasPaidMembership) {
        menuItems.push({
          title: "Membership Link",
          icon: "ion:ribbon",
          key: "membership_link",
          isDestructive: false,
          onPress: () => onClick(MassTextAttachmentId.MEMBERSHIP_LINK),
          disabled: !hasPaidMembership,
        });
      }

      if (attachmentSourceType === AttachmentSourceType.CONVERSATION) {
        menuItems.push({
          title: "Media",
          icon: "ion:image",
          key: "media",
          isDestructive: false,
          onPress: () => {
            if (
              !hasSubscriptionUnlockedAdvancedData(accountData) &&
              !accountData.isAdmin
            ) {
              dispatch(
                notificationActions.setMarkitPlusModal(PlanTypeV4.PRO.name)
              );
              return;
            }
            if (fileInputRef?.current) {
              fileInputRef.current.click();
            }
          },
        });
      }
    }

    return menuItems;
  }, [
    accountData,
    attachmentSourceType,
    creatorHasProducts,
    disabledEventLinks,
    dispatch,
    fileInputRef,
    hasPaidMembership,
    onClick,
  ]);

  return (
    <>
      <AlertContainer
        headerComp={
          <div className="ColumnNormal" style={{ gap: 4 }}>
            <Icon icon="mdi-image-remove" height={40} />
            <span style={{ fontSize: 16, fontWeight: "500" }}>{alertText}</span>
          </div>
        }
        subHeaderComp={alertSubText}
        theme={theme}
        closeModal={() => {
          setAlertText("");
          if (fileInputRef && fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }}
        hideModal={alertText === ""}
      />
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept="image/*"
        onChange={uploadImage}
      />
      <DropdownMenu
        dropdownItems={items}
        trigger={["click"]}
        placement="topLeft"
        overlayStyle={{ paddingBottom: 10 }}
        altWidth={220}
      >
        <div
          className={
            attachmentSourceType === AttachmentSourceType.CONVERSATION
              ? "CreatorChatAttachmentButton"
              : "MassTextsAttachmentButton"
          }
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          style={{
            backgroundColor: invertedColor
              ? theme?.ButtonSecondaryBG.backgroundColor
              : theme?.TertiaryBG.backgroundColor,
          }}
        >
          <Icon
            icon="ion:add"
            height={24}
            style={{
              color: invertedColor
                ? theme?.ButtonText.color
                : theme?.PrimaryText.color,
            }}
          />
        </div>
      </DropdownMenu>
    </>
  );
};
